import React, { useState } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent } from "./ui/card";
import { Phone, MapPin, Clock, MessageSquare, Send, CheckCircle2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { businessInfo } from "../data/mock";

const Contact = () => {
  const { toast } = useToast();
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", vehicle: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // FormSubmit.co — dead simple form-to-email. Change the email below to yours.
    // First submission triggers a confirmation email from FormSubmit; click the link once and you're done forever.
    const FORM_ENDPOINT = "https://formsubmit.co/ajax/hamiltonsmog315@gmail.com";

    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: `New message from ${form.name || "website visitor"} — Hamilton's Smog`,
          _template: "table",
          Name: form.name,
          Phone: form.phone,
          Email: form.email,
          Vehicle: form.vehicle,
          Message: form.message
        })
      });

      // Also keep a local backup copy
      const saved = JSON.parse(localStorage.getItem("hamiltons_leads") || "[]");
      saved.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem("hamiltons_leads", JSON.stringify(saved));

      if (res.ok) {
        setSent(true);
        toast({
          title: "Message sent!",
          description: "We'll be in touch shortly. For urgent requests, please call us."
        });
        setForm({ name: "", phone: "", email: "", vehicle: "", message: "" });
      } else {
        throw new Error("Submit failed");
      }
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please call us directly at (415) 493-8801.",
        variant: "destructive"
      });
    }
  };

  return (
    <section id="contact" className="py-20 md:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <Badge className="bg-green-50 text-green-700 border border-green-200 hover:bg-green-50 mb-4">Get in Touch</Badge>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight">Have a question? We're here to help.</h2>
            <p className="text-slate-600 mt-4 text-lg">Call, text, or send us a quick note. We respond fast during business hours.</p>

            <div className="mt-8 space-y-4">
              <a href={`tel:${businessInfo.phoneRaw}`} className="flex items-center gap-4 p-5 rounded-xl bg-white border border-slate-200 card-hover">
                <div className="w-12 h-12 rounded-lg bg-green-600 text-white grid place-items-center">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">Call us</div>
                  <div className="font-black text-slate-900 text-lg">{businessInfo.phone}</div>
                </div>
              </a>
              <a href={`sms:${businessInfo.textPhoneRaw}`} className="flex items-center gap-4 p-5 rounded-xl bg-white border border-slate-200 card-hover">
                <div className="w-12 h-12 rounded-lg bg-slate-900 text-white grid place-items-center">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">Call or Text</div>
                  <div className="font-black text-slate-900 text-lg">{businessInfo.textPhone}</div>
                </div>
              </a>
              <a href={businessInfo.directionsUrl} target="_blank" rel="noreferrer" className="flex items-center gap-4 p-5 rounded-xl bg-white border border-slate-200 card-hover">
                <div className="w-12 h-12 rounded-lg bg-slate-900 text-white grid place-items-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">Visit us</div>
                  <div className="font-black text-slate-900 text-lg">{businessInfo.address}</div>
                </div>
              </a>
              <div className="flex items-center gap-4 p-5 rounded-xl bg-white border border-slate-200">
                <div className="w-12 h-12 rounded-lg bg-slate-900 text-white grid place-items-center">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">Hours</div>
                  <div className="font-bold text-slate-900">Mon–Fri 8:30 AM – 5:30 PM • Sat 9–3 • Sun Closed</div>
                </div>
              </div>
            </div>
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardContent className="p-7 md:p-9">
              {sent ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 text-green-700 grid place-items-center">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900">Thanks — message received!</h3>
                  <p className="text-slate-600 mt-2">We'll get back to you shortly. For urgent needs, please call.</p>
                  <Button className="mt-6 bg-green-600 hover:bg-green-700" onClick={() => setSent(false)}>Send another</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h3 className="text-2xl font-black text-slate-900 mb-2">Send us a message</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="font-semibold text-slate-800">Name</Label>
                      <Input id="name" name="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="font-semibold text-slate-800">Phone</Label>
                      <Input id="phone" name="phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email" className="font-semibold text-slate-800">Email</Label>
                    <Input id="email" name="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="vehicle" className="font-semibold text-slate-800">Vehicle (year, make, model)</Label>
                    <Input id="vehicle" name="vehicle" placeholder="e.g. 2018 Toyota Camry" value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="message" className="font-semibold text-slate-800">How can we help?</Label>
                    <Textarea id="message" name="message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" />
                  </div>
                  <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold h-12 text-base">
                    <Send className="w-4 h-4 mr-2" /> Send Message
                  </Button>
                  <p className="text-xs text-slate-500 text-center">We respect your privacy. No spam, ever.</p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
